import {useMemo} from 'react'

import {normalizeFieldName, useMix} from '../../../../fields'
import {ChangeableFields} from '../types'

/**
 * Custom hook that applies material-specific business rules to changeable fields.
 * This hook enforces availability constraints for material-related fields based on
 * the current mix availability in the system.
 *
 * @param baseChangeableFields - Base changeable fields from useBaseChangeableFieldsRules
 * @returns Modified changeable fields with material availability rules applied
 *
 * Business Logic:
 * - Material-related fields become 'notChangeable' when no mixes are available
 * - This prevents users from selecting materials that cannot be fulfilled
 * - Applies to material, contract, and materialVariations fields
 * - Overrides time-based rules when availability constraints are not met
 *
 * Use Cases:
 * - Contract/quotation is no longer available for the managed order
 * - Order has no reference to the contract/quotation
 * - Contract has been cancelled or expired
 */
export const useMaterialChangeableFieldsRules = (
  baseChangeableFields: ChangeableFields
): ChangeableFields => {
  const {availableMixes} = useMix()

  // Check if any mixes are currently available for selection
  // When no mixes available, material fields should be locked regardless of timing
  const hasAvailableMixes = availableMixes.length > 0

  return useMemo(
    () =>
      baseChangeableFields.map((changeableField) => {
        const {fieldName, isDirty} = changeableField

        // Apply availability constraints to material-related fields
        // These fields depend on having available mixes to choose from
        if (
          ['material', 'contract', 'materialVariations'].includes(normalizeFieldName(fieldName).field) &&
          !hasAvailableMixes
        ) {
          return {
            fieldName,
            isDirty,
            state: 'notChangeable' // Override: block changes when no mixes available
          }
        }

        // Return unchanged for non-material fields or when mixes are available
        return changeableField
      }),
    [baseChangeableFields, hasAvailableMixes]
  )
}
